import React, { useState,useEffect } from "react";
import './Assessment.css';
import { removeToken } from '../../auth';
import { ToastContainer, toast } from 'react-toastify';
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppData } from "../../context/AppDataContext";
import { useApi } from  "../../context/ApiContext";
import { useQuery } from '@tanstack/react-query';
import ErrorBoundary from "../../components/ErrorBoundary";
import ThankYouScreen from "../../components/ThankYou";
import PageLoader from "../../components/PageLoader";

const columnScores = [-4, -3, -2, -1, 0, 1, 2, 3, 4];

export default function Assessment() {
  const [leftBoxes, setLeftBoxes] = useState([]);
  const [rightBoxes, setRightBoxes] = useState(Array(25).fill(null));
  const [showTutorial, setShowTutorial] = useState(true);
  const navigate = useNavigate();
  const { API, ready } = useApi();

  const { textData } = useAppData();
  const [scoreData, setScoreData] = useState({});
  const [errorCode, setErrorCode] = useState(null);
  const [thankYou, setThankYou] = useState(null);

  const [searchParams] = useSearchParams();
  const scoreID = searchParams.get("id");
  // FOR TESTING PURPOSES ONLY 1 POSSIBLE ID
  // const scoreID = "52F986B2EABDFB94C1258D00004A7580";
  const scoreQuery = useQuery({
    queryKey: ['scoreData', scoreID],
    queryFn: async () => {
      if (!scoreID) return null;

      const res = await API.get(`/document/${scoreID}?dataSource=rvcustdatascope`);
      if (res.data?.Form !== "Score") return null;
      return res.data || null;
    },
    enabled: !!scoreID && ready, // only run if participantID exists and API is ready
    retry: 1,
  });

  useEffect(() => {
    if (!scoreID) {setErrorCode("ASSESSMENT_INVALID_ID"); return;};
    if (!ready) return;
    if (scoreQuery.isLoading) return;

    const data = scoreQuery.data;
    if (!data || Object.keys(data).length === 0) {
      setThankYou("Access to update removed or assessment not found.")
      return;
    }

    const totalBoxesPlaced = leftBoxes.filter(Boolean).length + rightBoxes.filter(Boolean).length;
    const totalStatements = Object.keys(data).filter(key => key.startsWith('Statement_') && data[key]).length;

    if (totalBoxesPlaced >= totalStatements) return;

    setScoreData(data);

    const boxes = Array.from({ length: 25 }, (_, i) => {
      const key = `Statement_${i + 1}`;
      const text = data[key];
      return text ? { id: key, text } : null;
    });
    setLeftBoxes(boxes);
  }, [scoreQuery.data, scoreID, ready, scoreQuery.isLoading, leftBoxes, rightBoxes]);

  // const handleLogout = () => {
  //   removeToken();
  //   window.location.reload();
  // };

  const handleDragStart = (e, index, side) => {
    e.dataTransfer.setData("text/plain", `${side}-${index}`);
  };

  const handleDropToPyramid = (e, dropIndex) => {
    const data = e.dataTransfer.getData("text/plain");
    const [side, idx] = data.split("-");
    const sourceIndex = parseInt(idx, 10);
  
    if (side === "left") {
      const draggedBox = leftBoxes[sourceIndex];
  
      if (!draggedBox) return;
      
      const newRight = [...rightBoxes];
  

      let targetIndex = dropIndex;

      while (targetIndex < newRight.length && newRight[targetIndex]) {
        targetIndex++;
      }

      if (targetIndex >= newRight.length) {
        targetIndex = 0;
        while (targetIndex < newRight.length && newRight[targetIndex]) {
          targetIndex++;
        }
      }
  
      if (targetIndex < newRight.length) {
        const newLeft = leftBoxes.filter((_, i) => i !== sourceIndex); 
        newRight[targetIndex] = draggedBox;
  
        setLeftBoxes(newLeft);
        setRightBoxes(newRight);
      } else {
        console.warn("No empty slot available on the right.");
        toast.warn("No empty slot available on the right.")
      }
    }
  
    if (side === "right") {
      const draggedBox = rightBoxes[sourceIndex];
  
      if (!draggedBox) return;
  
      const newRight = [...rightBoxes];
  
      if (dropIndex === sourceIndex || newRight[dropIndex]) return;
  
      newRight[sourceIndex] = null;
      newRight[dropIndex] = draggedBox;
  
      setRightBoxes(newRight);
    }
  };
  
  

  const handleDropToLeft = (e) => {
    e.preventDefault();

    const data = e.dataTransfer.getData("text/plain");
    if (!data) return;
    const [side, idx] = data.split("-");
    const index = parseInt(idx, 10);

    if (side === "right") {
      const draggedBox = rightBoxes[index];
      if (!draggedBox) return;

      const newLeft = [...leftBoxes];
      const newRight = [...rightBoxes];
      newRight[index] = null;


      const emptyIndex = newLeft.findIndex((x) => x === null || x === undefined);

      if (emptyIndex !== -1) {
        newLeft[emptyIndex] = draggedBox;
      } else {
        newLeft.push(draggedBox);
      }

      setRightBoxes(newRight);
      setLeftBoxes(newLeft);
    }
  };

  const allowDrop = (e) => e.preventDefault();

  const rowPattern = [1, 3, 5, 7, 9];
  const createPyramidRows = (boxes) => {
    const rows = [];
    let index = 0;
    for (let count of rowPattern) {
      rows.push(boxes.slice(index, index + count));
      index += count;
    }
    return rows;
  };

  const pyramidRows = createPyramidRows(rightBoxes);

  const getColumnIndex = (rowIndex, boxIndex) => {
    const rowWidth = rowPattern[rowIndex];
    const offset = Math.floor((9 - rowWidth) / 2);
    return offset + boxIndex;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (leftBoxes.filter(Boolean).length > 0) {
      toast.error("Please place all boxes on the pyramid before submitting.");
      return;
    }

    const updatedData = { ...scoreData };

    for (let i = 1; i <= 25; i++) {
      const statementKey = `Statement_${i}`;
      const scoreKey = `Score_${i}`;
      const originalText = scoreData[statementKey];

      if (!originalText) continue;

      let found = false;
      for (let rowIndex = 0; rowIndex < pyramidRows.length; rowIndex++) {
        for (let boxIndex = 0; boxIndex < pyramidRows[rowIndex].length; boxIndex++) {
          const box = pyramidRows[rowIndex][boxIndex];
          if (box?.id === statementKey) {
            const columnIndex = getColumnIndex(rowIndex, boxIndex);
            updatedData[scoreKey] = columnScores[columnIndex];
            found = true;
            break;
          }
        }
        if (found) break;
      }

      if (!found) {
        updatedData[scoreKey] = null;
      }
    }
    API.put(`/document/${scoreID}?dataSource=rvcustdatascope`, updatedData).then(() => {
      // navigate("/thankyou");
      setThankYou("Thank you for participating in our Dynamic Profiler. You may now close this window.");
    }).catch(() => {
      // setErrorCode("SERVER_ERROR");
      toast.error("Error submitting pyramid. Please try again.");
    });
  };
  
  const handleBoxClick = (index) => {
    const box = rightBoxes[index];
    if (!box) return;
  
    const newLeft = [...leftBoxes, box]; 
    const newRight = [...rightBoxes];
    newRight[index] = null;
  
    setLeftBoxes(newLeft);
    setRightBoxes(newRight);
  };

  if (!ready || scoreQuery.isLoading) {
    return <PageLoader />; // show loader until API is ready and data fetched
  }

  if (errorCode) {
      return <ErrorBoundary code={errorCode} />;
  }

  if (thankYou) {
    return <ThankYouScreen message={thankYou} />;
  }
  
  return (
  <div className="page-wrapper">
    <div className="header">
      <div className="header-inner">
        <div className="header-left">
          <img src="/app/Image/MainLogo.png" alt="Logo" className="logo" />
        </div>
        <div className="header-center">
          <span className="header-title">Corporate Dynamic Profiler™</span>
        </div>
        <div className="header-right">
          <button className="header-btn" onClick={() => setShowTutorial(true)}>Tutorial</button>
          {/* <button className="header-btn" onClick={handleLogout}>Sign Out</button> */}
          <button className="header-btn-submit" onClick={handleSubmit}>SUBMIT</button>
        </div>
      </div>
    </div>
    <div className="container">
      <div className="container-inner">
        <div className="left-panel">
          <div className="select-statement-section">
            {textData.length > 0 ? (
              <>
                <div className="select-header">{textData[0].select.title}</div>
                <div className="select-content">
                  <p dangerouslySetInnerHTML={{ __html: textData[0].select.body }} />
                </div>
              </>
            ) : (
              <></>
            )}
            {/* <div className="select-header">Select Statement Here</div>
            <div className="select-content">
              <p>
                The following statements represent the<br/>
                priorities for your company. Select and drag<br/>
                statements from here onto the pyramid.<br/>
              </p>
            </div> */}
          </div>
          <div className="column left" onDrop={handleDropToLeft} onDragOver={allowDrop}>
            {leftBoxes.map((box, index) =>
              box ? (
                <div
                  key={box.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index, "left")}
                  className="draggable-box"
                >
                  {box.text}
                </div>
              ) : (
                <div key={index} className="empty-box" />
              )
            )}
          </div>
        </div>
        

        <div className="center-divider"></div>

        <div className="column right" >
          <div className="sort-statement-section">
              {textData.length > 0 ? (
                <>
                  <div className="sort-header">{textData[0].sort.title}</div>
                  <div className="sort-content">
                    <p dangerouslySetInnerHTML={{ __html: textData[0].sort.body }} />
                  </div>
                </>
              ) : (
                <></>
              )}
              {/* <div className="sort-header">Sort the Statement Here</div>
              <div className="sort-content">
                <p>
                  Sort these 25 statements on the pyramid from the most<br/>
                  important at the right of the pyramid (+4) to the least<br/>
                  important bottom left (-4)<br/><br/>
                  You can move the statement around on the pyramid as often has
                  needed until you are satisfied. There is no time limitation for
                  this task.<br/><br/>
                  Please note that the sorting within a column of the pyramid has
                  no relevance regarding the statements importance.<br/><br/>
                  Once you are done, please click the submit button.
                </p>
              </div> */}
          </div>
            
          <div className="pyramid-wrapper">
            <div className="pyramid-rows" >
              {pyramidRows.map((row, rowIndex) => {
                const rowWidth = rowPattern[rowIndex];
                const offset = Math.floor((9 - rowWidth) / 2);
                const flatStart = pyramidRows
                  .slice(0, rowIndex)
                  .reduce((acc, r) => acc + r.length, 0);

                const cells = [];

                for (let i = 0; i < offset; i++) {
                  cells.push(<div className="pyramid-box invisible" key={`el${i}`} />);
                }

                for (let i = 0; i < row.length; i++) {
                  const flatIndex = flatStart + i;
                  const box = row[i];

                  cells.push(
                    <div
                    key={flatIndex}
                    onDrop={(e) => handleDropToPyramid(e, flatIndex)}
                    onDragOver={allowDrop}
                    onClick={() => handleBoxClick(flatIndex)}
                    draggable={!!box}
                    onDragStart={(e) => handleDragStart(e, flatIndex, "right")}
                    className={`pyramid-box ${box ? "filled" : ""}`}
                  >
                    {box?.text || ""}
                  </div>
                  );
                }

                for (let i = 0; i < 9 - offset - row.length; i++) {
                  cells.push(<div className="pyramid-box invisible" key={`er${i}`} />);
                }

                  return (
                    <div className="pyramid-grid" key={rowIndex}>
                      {cells}
                    </div>
                  );
                })}

              <div></div>
            </div>
          </div>
          <div className="pyramid-grid score-row ">
            {columnScores.map((score, i) => (
              <div key={i} className="score-cell">
                {score}
              </div>
            ))}
          </div>
        </div>
        <ToastContainer />
      </div>
    </div>
    {showTutorial && (
      <div className="modal-overlay" onClick={() => setShowTutorial(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={() => setShowTutorial(false)}>✕</button>
          <h2 className="modal-title">Corporate Dynamic Profiler</h2>
          <h1 className="modal-welcome">WELCOME</h1>
          <div className="modal-text">
              <p>
                Before you begin, please watch this video to learn how to<br/>
                navigate and use the platform.
              </p>
          </div>
          <video controls className="modal-video">
            <source src="/app/Video/TutorialVideo.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    )}
  </div>
  );
};

